import type { User, UserCreatePayload, UserUpdatePayload } from "../types/user";
import { API_BASE, apiFetch, getAuthHeaders, handleUnauthorized, parseApiError } from "./client";

export async function listUsers(): Promise<User[]> {
  return apiFetch<User[]>("/api/v1/admin/users");
}

export async function getUser(id: string): Promise<User> {
  return apiFetch<User>(`/api/v1/admin/users/${id}`);
}

export async function createUser(
  payload: UserCreatePayload,
  signature?: File,
): Promise<User> {
  const headers = (await getAuthHeaders()) as Record<string, string>;
  delete headers["Content-Type"];

  const formData = new FormData();
  formData.append("name", payload.name);
  formData.append("surname", payload.surname);
  formData.append("role", payload.role);
  formData.append("email", payload.email);
  if (signature) {
    formData.append("signature", signature);
  }

  const response = await fetch(`${API_BASE}/api/v1/admin/users`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (response.status === 401) return handleUnauthorized(response);
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(parseApiError(response.status, body));
  }
  return response.json() as Promise<User>;
}

export async function updateUser(
  id: string,
  payload: UserUpdatePayload,
  signature?: File,
): Promise<User> {
  const headers = (await getAuthHeaders()) as Record<string, string>;
  delete headers["Content-Type"];

  const formData = new FormData();
  if (payload.name !== undefined) formData.append("name", payload.name);
  if (payload.surname !== undefined) formData.append("surname", payload.surname);
  if (signature) {
    formData.append("signature", signature);
  }

  const response = await fetch(`${API_BASE}/api/v1/admin/users/${id}`, {
    method: "PUT",
    headers,
    body: formData,
  });
  if (response.status === 401) return handleUnauthorized(response);
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(parseApiError(response.status, body));
  }
  return response.json() as Promise<User>;
}

export async function disableUser(id: string): Promise<void> {
  await apiFetch<void>(`/api/v1/admin/users/${id}/disable`, {
    method: "PATCH",
  });
}

export async function enableUser(id: string): Promise<void> {
  await apiFetch<void>(`/api/v1/admin/users/${id}/enable`, {
    method: "PATCH",
  });
}

export async function resendInvite(id: string): Promise<void> {
  await apiFetch<void>(`/api/v1/admin/users/${id}/resend-invite`, {
    method: "POST",
  });
}

export async function resetUserPassword(id: string): Promise<void> {
  await apiFetch<void>(`/api/v1/admin/users/${id}/reset-password`, {
    method: "POST",
  });
}
